import { apiSlice } from "./api";

export const credentialingApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    
    // Credentialing Dashboard - Get user's overall badge progress
    getCredentialingDashboard: builder.query({
      query: () => ({
        url: "api/credentialing/dashboard/",
        method: "GET",
      }),
      providesTags: ["CredentialingDashboard", "BadgeProgress"],
    }),

    // Get specific badge module content and progress
    getBadgeModule: builder.query({
      query: (moduleId) => ({
        url: `api/credentialing/modules/${moduleId}/`,
        method: "GET",
      }),
      providesTags: (result, error, moduleId) => [
        { type: "BadgeModule", id: moduleId },
        "BadgeProgress",
      ],
    }),

    // Retrieve badge artwork assets (icon + large image)
    getBadgeMedia: builder.query({
      query: (badgeId) => ({
        url: `api/credentialing/badges/${badgeId}/media/`,
        method: "GET",
      }),
      transformResponse: (response) => {
        if (!response || typeof response !== 'object') {
          return response;
        }
        // API may wrap payload in { data: {...} } – flatten to keep consumer simple
        if (response.data && typeof response.data === 'object') {
          return response.data;
        }
        return response;
      },
      providesTags: (result, error, badgeId) => [
        { type: "BadgeMedia", id: badgeId },
      ],
    }),

    // Complete a badge module
    completeBadgeModule: builder.mutation({
      query: ({ moduleId, reflection, notes, timeSpent }) => ({
        url: `api/credentialing/modules/${moduleId}/complete/`,
        method: "POST",
        body: {
          reflection: reflection || "",
          notes: notes || "",
          time_spent: timeSpent || 0,
        },
      }),
      invalidatesTags: [
        "CredentialingDashboard",
        "BadgeProgress", 
        "TrainingDashboard", 
        "TrainingProgress",
        (result, error, { moduleId }) => ({ type: "TrainingModule", id: moduleId }),
        (result, error, { moduleId }) => ({ type: "BadgeModule", id: moduleId }),
      ],
    }),

    // Save reflection and notes without completing module
    saveModuleReflection: builder.mutation({
      query: ({ moduleId, reflection, notes, timeSpent }) => ({
        url: `api/credentialing/modules/${moduleId}/reflection/`,
        method: "POST",
        body: {
          reflection: reflection || "",
          notes: notes || "",
          time_spent: timeSpent || 0,
        },
      }),
      invalidatesTags: [
        "TrainingProgress",
        (result, error, { moduleId }) => ({ type: "TrainingModule", id: moduleId }),
      ],
    }),

    // Import training content (staff only)
    importBadgeContent: builder.mutation({
      query: ({ contentSource, contentFormat, updateExisting }) => ({
        url: "api/credentialing/content/import/",
        method: "POST",
        body: {
          content_source: contentSource,
          content_format: contentFormat,
          update_existing: updateExisting || false,
        },
      }),
      invalidatesTags: ["TrainingDashboard", "TrainingModule"],
    }),

    // Export training content
    exportBadgeContent: builder.query({
      query: ({ moduleId, format }) => ({
        url: `api/credentialing/content/export/${moduleId}/`,
        method: "GET",
        params: { format: format || "html" },
        responseHandler: (response) => response.blob(), // Handle file download
      }),
    }),

    // Get training analytics (staff only)
    getCredentialingAnalytics: builder.query({
      query: () => ({
        url: "api/credentialing/analytics/",
        method: "GET",
      }),
      providesTags: ["TrainingAnalytics"],
    }),

    // Submit impact story for Impact Champion badge
    submitImpactStory: builder.mutation({
      query: ({ title, description, grade_level }) => ({
        url: "api/credentialing/impact-story/submit/",
        method: "POST",
        body: { title, description, grade_level },
      }),
      invalidatesTags: ["TrainingDashboard", "TrainingProgress", "ImpactStories", "CredentialingDashboard", "BadgeProgress"],
    }),

    // Get user's submitted impact stories
    getUserImpactStories: builder.query({
      query: () => ({
        url: "api/credentialing/impact-stories/",
        method: "GET",
      }),
      providesTags: ["ImpactStories"],
    }),


    // Submit training quiz
    submitModuleQuiz: builder.mutation({
      query: ({ moduleId, responses }) => ({
        url: "api/credentialing/quiz/submit/",
        method: "POST",
        body: {
          module_id: moduleId,
          responses: responses,
        },
      }),
      invalidatesTags: (result, error, { moduleId }) => [
        "CredentialingDashboard",
        "BadgeProgress",
        "TrainingDashboard", 
        "TrainingProgress",
        { type: "TrainingModule", id: moduleId },
        { type: "BadgeModule", id: moduleId },
      ],
    }),

    // Get quiz results
    getQuizResults: builder.query({
      query: (moduleId) => ({
        url: `api/credentialing/quiz/results/${moduleId}/`,
        method: "GET",
      }),
      providesTags: (result, error, moduleId) => [
        { type: "QuizResults", id: moduleId },
      ],
    }),

    // Update video progress
    updateVideoProgress: builder.mutation({
      query: ({ moduleId, currentTime, duration, eventType = 'progress' }) => ({
        url: `api/credentialing/modules/${moduleId}/video-progress/`,
        method: "POST",
        body: {
          current_time: currentTime,
          duration: duration,
          event_type: eventType,
        },
      }),
      invalidatesTags: (result, error, { moduleId }) => [
        "CredentialingDashboard",
        "BadgeProgress",
        { type: "BadgeModule", id: moduleId },
      ],
    }),

    // Update content reading progress
    updateContentProgress: builder.mutation({
      query: ({ moduleId, scroll_percentage, reading_time }) => ({
        url: `api/credentialing/modules/${moduleId}/content-progress/`,
        method: "POST",
        body: {
          scroll_percentage,
          reading_time,
        },
      }),
      invalidatesTags: (result, error, { moduleId }) => [
        "CredentialingDashboard",
        "BadgeProgress",
        { type: "BadgeModule", id: moduleId },
      ],
    }),

    // Submit module reflection
    submitModuleReflection: builder.mutation({
      query: ({ moduleId, reflection, timeSpent = 0 }) => ({
        url: `api/credentialing/modules/${moduleId}/submit-reflection/`,
        method: "POST",
        body: {
          reflection: reflection,
          time_spent: timeSpent,
        },
      }),
      invalidatesTags: (result, error, { moduleId }) => [
        "CredentialingDashboard",
        "BadgeProgress",
        { type: "BadgeModule", id: moduleId },
      ],
    }),

    // Get module completion status
    getModuleCompletionStatus: builder.query({
      query: (moduleId) => ({
        url: `api/credentialing/modules/${moduleId}/completion-status/`,
        method: "GET",
      }),
      providesTags: (result, error, moduleId) => [
        { type: "ModuleCompletionStatus", id: moduleId },
      ],
    }),

  }),
});

// Export hooks for use in components
export const {
  useGetCredentialingDashboardQuery,
  useGetBadgeModuleQuery,
  useGetBadgeMediaQuery,
  useCompleteBadgeModuleMutation,
  useSaveModuleReflectionMutation,
  useSubmitModuleQuizMutation,
  useGetQuizResultsQuery,
  useImportBadgeContentMutation,
  useLazyExportBadgeContentQuery,
  useGetCredentialingAnalyticsQuery,
  useSubmitImpactStoryMutation,
  useGetUserImpactStoriesQuery,
  useUpdateVideoProgressMutation,
  useUpdateContentProgressMutation,
  useSubmitModuleReflectionMutation,
  useGetModuleCompletionStatusQuery,
} = credentialingApiSlice;
